

import React, { useState, useEffect, useRef } from 'react';
import { Company } from '../types';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
import { XMarkIcon } from './icons/XMarkIcon'; // For removing logo

interface CompanyFormProps {
  initialCompany?: Company | Partial<Company> | null;
  onSubmit: (companyData: Company) => void;
  onCancel: () => void;
  isEditing: boolean;
}

interface IBGEState {
  id: number;
  sigla: string;
  nome: string;
}

interface IBGECity {
  id: number;
  nome: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const initialFormState: Company = {
  id: '',
  name: '',
  cnpj: '',
  logoUrl: '',
  address: {
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '', 
    state: '', 
    zipCode: '',
  },
  phone: '',
  email: '',
  website: '',
  focalPoint: {
    name: '',
    role: '',
    email: '',
    phone: '',
  },
};

export const CompanyForm: React.FC<CompanyFormProps> = ({ initialCompany, onSubmit, onCancel, isEditing }) => {
  const [formData, setFormData] = useState<Company>(initialFormState);
  
  const [statesList, setStatesList] = useState<SelectOption[]>([]);
  const [citiesList, setCitiesList] = useState<SelectOption[]>([]);
  
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const [loadingCities, setLoadingCities] = useState<boolean>(false);
  
  const [errorStates, setErrorStates] = useState<string | null>(null);
  const [errorCities, setErrorCities] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      setErrorStates(null);
      try {
        const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: IBGEState[] = await response.json();
        setStatesList(data.map(state => ({ value: state.sigla, label: state.nome })));
      } catch (error) {
        console.error("Failed to fetch states:", error);
        setErrorStates("Falha ao carregar estados. Tente novamente.");
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const selectedUF = formData.address.state;
    if (selectedUF) {
      const fetchCities = async () => {
        setLoadingCities(true);
        setErrorCities(null);
        // Não limpar a lista de cidades aqui para evitar que o campo pisque vazio durante a edição.
        // A lista será substituída após a busca bem-sucedida.
        try {
          const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${selectedUF}/municipios?orderBy=nome`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data: IBGECity[] = await response.json();
          setCitiesList(data.map(city => ({ value: city.nome, label: city.nome })));
        } catch (error) {
          console.error("Failed to fetch cities:", error);
          setErrorCities("Falha ao carregar cidades. Tente novamente.");
          setCitiesList([]); // Limpar a lista apenas em caso de erro.
        } finally {
          setLoadingCities(false);
        }
      };
      fetchCities();
    } else {
      setCitiesList([]); 
    }
  }, [formData.address.state]);

  useEffect(() => {
    if (initialCompany) {
      const mergedCompany: Company = {
        ...initialFormState,
        ...initialCompany,
        logoUrl: initialCompany.logoUrl || '',
        address: {
          ...initialFormState.address,
          ...(initialCompany.address || {}),
        },
        focalPoint: {
          ...initialFormState.focalPoint,
          ...(initialCompany.focalPoint || {}),
        },
      } as Company;
      
      // Se estiver editando e a cidade existir, mas a lista de cidades ainda não foi carregada,
      // criamos uma lista temporária com apenas essa cidade para que o <Select> possa exibir o valor imediatamente.
      // O useEffect que escuta `formData.address.state` irá substituir esta lista pela lista completa.
      if (mergedCompany.address.city && citiesList.length === 0) {
          setCitiesList([{ value: mergedCompany.address.city, label: mergedCompany.address.city }]);
      }
      
      setFormData(mergedCompany);
    } else {
      setFormData(initialFormState);
      setCitiesList([]);
    }
  }, [initialCompany]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'state') {
      // Ao mudar o estado, limpa a cidade selecionada para forçar uma nova seleção
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, state: value, city: '' }, 
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [name]: value },
      }));
    }
  };

  const handleFocalPointChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      focalPoint: { ...(prev.focalPoint || initialFormState.focalPoint), [name]: value },
    }) as Company);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logoUrl: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logoUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset file input
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('O campo "Nome da Empresa" é obrigatório.');
      return;
    }
    if (!formData.address.state || !formData.address.city) {
      alert('Estado e Cidade são obrigatórios.');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-dark-text-primary mb-3">Informações Gerais</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Nome da Empresa*" name="name" value={formData.name} onChange={handleChange} required />
          <Input label="CNPJ" name="cnpj" value={formData.cnpj || ''} onChange={handleChange} />
        </div>
        
        <div className="mt-4">
          <label className="block text-sm font-medium text-dark-text-secondary mb-1">
            Logo da Empresa
          </label>
          <div className="mt-1 flex items-center space-x-4">
            <div className="flex-shrink-0 h-20 w-20 rounded-md bg-dark-tertiary flex items-center justify-center overflow-hidden">
              {formData.logoUrl ? (
                <img src={formData.logoUrl} alt="Logo preview" className="h-full w-full object-contain" />
              ) : (
                <span className="text-xs text-dark-text-secondary text-center">Sem logo</span>
              )}
            </div>
            <div className="flex flex-col space-y-2">
              <input
                type="file"
                name="logoFile"
                id="logoFile"
                accept="image/*"
                onChange={handleLogoChange}
                ref={fileInputRef}
                className="block w-full text-sm text-dark-text-secondary
                           file:mr-4 file:py-2 file:px-4
                           file:rounded-md file:border-0
                           file:text-sm file:font-semibold
                           file:bg-brand-teal file:text-white
                           hover:file:bg-opacity-80
                           cursor-pointer"
              />
              {formData.logoUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={removeLogo} leftIcon={<XMarkIcon className="text-base"/>}>
                  Remover logo
                </Button>
              )}
            </div>
          </div>
           {/* Hidden input to satisfy any potential 'logoUrl' field if needed elsewhere, though base64 is stored */}
           <input type="hidden" name="logoUrl" value={formData.logoUrl || ''} />
        </div>

      </div>

      <div>
        <h4 className="text-lg font-medium text-dark-text-primary mb-3">Endereço</h4>
        <Input label="Rua*" name="street" value={formData.address.street} onChange={handleAddressChange} required />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Número" name="number" value={formData.address.number || ''} onChange={handleAddressChange} />
          <Input label="Complemento" name="complement" value={formData.address.complement || ''} onChange={handleAddressChange} />
          <Input label="Bairro*" name="neighborhood" value={formData.address.neighborhood} onChange={handleAddressChange} required />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Estado (UF)*"
            name="state"
            value={formData.address.state}
            onChange={handleAddressChange}
            options={statesList}
            placeholderOption={loadingStates ? "Carregando estados..." : "Selecione um Estado"}
            disabled={loadingStates || statesList.length === 0}
            error={errorStates || undefined}
            required
          />
          <Select
            label="Cidade*"
            name="city"
            value={formData.address.city}
            onChange={handleAddressChange}
            options={citiesList}
            placeholderOption={
              loadingCities ? "Carregando cidades..." : 
              !formData.address.state ? "Selecione um Estado primeiro" : 
              citiesList.length === 0 && !errorCities && !loadingCities ? "Nenhuma cidade encontrada" :
              "Selecione uma Cidade"
            }
            disabled={loadingCities || !formData.address.state || (citiesList.length === 0 && !errorCities && !loadingCities)}
            error={errorCities || undefined}
            required
          />
          <Input label="CEP*" name="zipCode" value={formData.address.zipCode} onChange={handleAddressChange} required />
        </div>
      </div>

      <div>
        <h4 className="text-lg font-medium text-dark-text-primary mb-3">Contato</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Telefone" name="phone" type="tel" value={formData.phone || ''} onChange={handleChange} />
          <Input label="E-mail" name="email" type="email" value={formData.email || ''} onChange={handleChange} />
        </div>
        <Input label="Website" name="website" type="url" value={formData.website || ''} onChange={handleChange} />
      </div>

      <div>
        <h4 className="text-lg font-medium text-dark-text-primary mb-3">Ponto Focal (Contato Principal)</h4>
         <Input label="Nome do Contato" name="name" value={formData.focalPoint?.name || ''} onChange={handleFocalPointChange} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Cargo" name="role" value={formData.focalPoint?.role || ''} onChange={handleFocalPointChange} />
          <Input label="E-mail do Contato" name="email" type="email" value={formData.focalPoint?.email || ''} onChange={handleFocalPointChange} />
        </div>
        <Input label="Telefone do Contato" name="phone" type="tel" value={formData.focalPoint?.phone || ''} onChange={handleFocalPointChange} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" isLoading={loadingStates || loadingCities}>
          {isEditing ? 'Salvar Alterações' : 'Adicionar Empresa'}
        </Button>
      </div>
    </form>
  );
};